/**
 * F0.3 — Org Positioning Tab
 * Mandate assessment, positioning statements, competitive context.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { useOrgPositioning, useUpdateOrgPositioning } from "../../hooks/useProfile";
import { useCompetitionSummary, useDirectCompetitors } from "../../hooks/useFundMatch";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Lock, Unlock, AlertCircle, Loader2, CheckCircle2, Edit3, Save, X,
  Target, Globe, Users, BarChart3, Zap, MapPin, Swords,
} from "lucide-react";

const MANDATE_COLOR = {
  FIXED:       { border: "border-red-200",    bg: "bg-red-50",    badge: "bg-red-100 text-red-700",    icon: Lock,   label: "FIXED" },
  CONSTRAINED: { border: "border-amber-200",  bg: "bg-amber-50",  badge: "bg-amber-100 text-amber-700", icon: AlertCircle, label: "CONSTRAINED" },
  FLEXIBLE:    { border: "border-green-200",  bg: "bg-green-50",  badge: "bg-green-100 text-green-700", icon: Unlock, label: "FLEXIBLE" },
};

const GEO_SCOPE_OPTIONS = ["LOCAL","REGIONAL","PROVINCIAL","NATIONAL","INTERNATIONAL"];
const LANG_OPTIONS      = ["ENGLISH","FRENCH","BILINGUAL","MULTILINGUAL","OTHER"];
const DELIVERY_OPTIONS  = ["DIRECT_SERVICE","GRANTING","ADVOCACY","RESEARCH","HYBRID"];
const RELIGIOUS_OPTIONS = ["SECULAR","FAITH_BASED","INDIGENOUS_SPIRITUAL"];
const INDIG_OPTIONS     = ["INDIGENOUS_LED","INDIGENOUS_SERVING","NEITHER"];

const TARGET_POPULATIONS = [
  "Children/Youth","Seniors","People with Disabilities","Homeless/At-risk",
  "Newcomers/Immigrants","Indigenous Peoples","Women","LGBTQ+",
  "Racialized Communities","General Public","Specific Ethnic/Cultural","Animals/Environment",
];

function MultiSelect({ options, selected = [], onChange, label }) {
  const sel = Array.isArray(selected) ? selected : [];
  const toggle = (v) => {
    if (sel.includes(v)) onChange(sel.filter(x => x !== v));
    else onChange([...sel, v]);
  };
  return (
    <div>
      <p className="text-xs text-zinc-500 mb-2">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(v => (
          <button key={v} onClick={() => toggle(v)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              sel.includes(v)
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-teal-300"
            }`}>
            {v.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    </div>
  );
}

function SingleSelect({ options, value, onChange, label }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 mb-2">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(v => (
          <button key={v} onClick={() => onChange(v)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              value === v
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-teal-300"
            }`}>
            {v.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    </div>
  );
}

function MandateDialCard({ mandate }) {
  if (!mandate) return null;
  const cfg = MANDATE_COLOR[mandate.restrictiveness] || MANDATE_COLOR.CONSTRAINED;
  const Icon = cfg.icon;
  const pct = Math.round((mandate.breadth_score || 0.5) * 100);
  return (
    <Card className={`border ${cfg.border} ${cfg.bg}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5" />
          Mandate Assessment
        </CardTitle>
        <CardDescription>Derived from CRA activity codes and mission</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Badge className={`text-sm px-3 py-1 ${cfg.badge}`}>{cfg.label}</Badge>
          <span className="text-xs text-zinc-500">Breadth: {pct}%</span>
        </div>
        <div>
          <div className="flex justify-between text-xs text-zinc-500 mb-1">
            <span>Single-issue</span><span>Broad</span>
          </div>
          <Progress value={pct} className="h-3" />
        </div>
        <p className="text-sm text-zinc-700 leading-relaxed">{mandate.plain_language}</p>
        {mandate.allowed_project_types?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-zinc-500 uppercase mb-2">Eligible Project Types</p>
            <div className="flex flex-wrap gap-1.5">
              {mandate.allowed_project_types.map(t => (
                <Badge key={t} variant="outline" className="text-xs capitalize">{t}</Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PositioningTab() {
  const { user } = useAuth();
  const bn = user?.charity_bn || localStorage.getItem("impactplexus_charity_bn");
  const { data, isLoading, error, refetch } = useOrgPositioning(bn);
  const update = useUpdateOrgPositioning(bn);

  const { data: compSummary } = useCompetitionSummary(bn, !!bn);
  const { data: directCompetitors } = useDirectCompetitors(bn, 5, !!bn);

  const STAGE_INFO = {
    mirror:     { label: "Mirror",     color: "bg-red-100 text-red-700 border-red-200",     desc: "Identical mandate — funders must choose between you" },
    direct:     { label: "Direct",     color: "bg-orange-100 text-orange-700 border-orange-200", desc: "Same end goal, overlapping population" },
    method_alt: { label: "Method Alt", color: "bg-amber-100 text-amber-700 border-amber-200",  desc: "Same goal, different approach — can coexist" },
    indirect:   { label: "Indirect",   color: "bg-blue-100 text-blue-700 border-blue-200",    desc: "Same sector, different outcome" },
    adjacent:   { label: "Adjacent",   color: "bg-zinc-100 text-zinc-600 border-zinc-200",    desc: "Tangential overlap only" },
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft({
      service_geography_scope: data?.l1?.service_geography_scope,
      target_populations:      data?.l1?.target_populations || [],
      language_of_service:     data?.l1?.language_of_service,
      program_delivery_model:  data?.l1?.program_delivery_model || [],
      religious_affiliation:   data?.l1?.religious_affiliation,
      indigenous_governance:   data?.l1?.indigenous_governance,
      accreditations:          data?.l1?.accreditations || [],
      positioning_primary:     data?.positioning?.primary || "",
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await update.mutateAsync(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!bn) return (
    <Alert className="border-amber-200 bg-amber-50">
      <AlertCircle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="text-amber-800">
        No charity BN linked to your account. Add your BN in Settings to enable positioning.
      </AlertDescription>
    </Alert>
  );

  return (
    <div className="space-y-6" data-testid="positioning-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-zinc-900">
            {data?.entity_name || bn}
          </h2>
          <p className="text-sm text-zinc-500">Org Identity & Competitive Positioning · BN: {bn}</p>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <div className="text-right">
              <p className="text-xs text-zinc-500">Profile Completeness</p>
              <div className="flex items-center gap-2">
                <Progress value={data.profile_completeness} className="w-24 h-2" />
                <span className="text-sm font-semibold text-zinc-700">{data.profile_completeness}%</span>
              </div>
            </div>
          )}
          {!editing ? (
            <Button size="sm" variant="outline" onClick={startEdit} disabled={!data}>
              <Edit3 className="h-3 w-3 mr-1" /> Edit Details
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                <X className="h-3 w-3 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={save} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin" />Loading positioning data...
        </div>
      )}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertDescription className="text-red-800">{error.message}</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left column */}
          <div className="space-y-6">
            {/* Mandate Assessment */}
            <MandateDialCard mandate={data.mandate} />

            {/* Legal Identity (L0 — read-only) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Lock className="h-4 w-4 text-zinc-400" />
                  Legal Identity
                  <Badge variant="outline" className="text-xs ml-1">CRA — Read-only</Badge>
                </CardTitle>
                <CardDescription>Immutable CRA T3010 data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: "Charity Class",    value: data.l0.charity_classification?.replace(/_/g," ") },
                  { label: "HQ",               value: `${data.l0.hq_city || "—"}, ${data.l0.hq_province || "—"}` },
                  { label: "Revenue Band",     value: data.l0.revenue_band },
                  { label: "Designation Age",  value: data.l0.designation_age_years ? `${data.l0.designation_age_years} yrs` : "—" },
                  { label: "Fiscal Year",      value: data.l0.fiscal_year },
                ].map(r => (
                  <div key={r.label} className="flex justify-between text-sm py-1.5 border-b border-zinc-100 last:border-0">
                    <span className="text-zinc-500">{r.label}</span>
                    <span className="font-medium text-zinc-800">{r.value || "—"}</span>
                  </div>
                ))}
                {data.l0.cra_activity_codes?.length > 0 && (
                  <div className="pt-1">
                    <p className="text-xs text-zinc-500 mb-1.5">CRA Activity Codes</p>
                    <div className="flex flex-wrap gap-1">
                      {data.l0.cra_activity_codes.map(c => (
                        <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Org Details — L1 editable */}
            <Card className={editing ? "border-teal-300 ring-1 ring-teal-200" : ""}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Edit3 className="h-4 w-4 text-teal-600" />
                  Org Details
                  <Badge className="bg-teal-100 text-teal-700 text-xs ml-1">Editable</Badge>
                </CardTitle>
                <CardDescription>These fields affect funder eligibility filters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <SingleSelect options={GEO_SCOPE_OPTIONS} value={draft.service_geography_scope}
                      onChange={v => setDraft(d => ({...d, service_geography_scope: v}))}
                      label="Service Geography Scope" />
                    <SingleSelect options={LANG_OPTIONS} value={draft.language_of_service}
                      onChange={v => setDraft(d => ({...d, language_of_service: v}))}
                      label="Language of Service" />
                    <MultiSelect options={DELIVERY_OPTIONS} selected={draft.program_delivery_model}
                      onChange={v => setDraft(d => ({...d, program_delivery_model: v}))}
                      label="Program Delivery Model" />
                    <SingleSelect options={RELIGIOUS_OPTIONS} value={draft.religious_affiliation}
                      onChange={v => setDraft(d => ({...d, religious_affiliation: v}))}
                      label="Religious / Secular Affiliation" />
                    <SingleSelect options={INDIG_OPTIONS} value={draft.indigenous_governance}
                      onChange={v => setDraft(d => ({...d, indigenous_governance: v}))}
                      label="Indigenous Governance Status" />
                    <MultiSelect options={TARGET_POPULATIONS} selected={draft.target_populations}
                      onChange={v => setDraft(d => ({...d, target_populations: v}))}
                      label="Target Populations" />
                  </>
                ) : (
                  <div className="space-y-2">
                    {[
                      { icon: Globe,  label: "Service Geography", value: data.l1.service_geography_scope },
                      { icon: Zap,    label: "Language",          value: data.l1.language_of_service },
                      { icon: Target, label: "Delivery Model",    value: (data.l1.program_delivery_model||[]).join(", ") || "—" },
                      { icon: Lock,   label: "Religious",         value: data.l1.religious_affiliation },
                      { icon: MapPin, label: "Indigenous Gov.",   value: data.l1.indigenous_governance },
                    ].map(r => (
                      <div key={r.label} className="flex items-center gap-3 text-sm py-1.5 border-b border-zinc-100 last:border-0">
                        <r.icon className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                        <span className="text-zinc-500 w-36">{r.label}</span>
                        <span className="font-medium text-zinc-800 capitalize">{(r.value||"—").toLowerCase().replace(/_/g," ")}</span>
                      </div>
                    ))}
                    {data.l1.target_populations?.length > 0 && (
                      <div className="pt-2">
                        <p className="text-xs text-zinc-500 mb-1.5 flex items-center gap-1"><Users className="h-3 w-3" /> Target Populations</p>
                        <div className="flex flex-wrap gap-1">
                          {data.l1.target_populations.map(t => (
                            <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Primary Positioning Statement */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Positioning Statement</CardTitle>
                <CardDescription>How you present your org to funders (200 chars max)</CardDescription>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <textarea
                    maxLength={200}
                    className="w-full text-sm border border-zinc-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-teal-400 h-24"
                    placeholder="Describe your org's key strengths and focus for funders..."
                    value={draft.positioning_primary || ""}
                    onChange={e => setDraft(d => ({...d, positioning_primary: e.target.value}))}
                  />
                ) : (
                  <p className="text-sm text-zinc-700 leading-relaxed">
                    {data.positioning?.primary || (
                      <span className="text-zinc-400 italic">No positioning statement yet — click Edit Details to add one.</span>
                    )}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Competitive Context */}
            {(data.competitive?.competitive_density_score != null || data.competitive?.distinctiveness_score != null) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="h-4 w-4 text-violet-600" />
                    Competitive Context
                  </CardTitle>
                  <CardDescription>How you compare to peer orgs (nightly update)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-600">Competitive Density</span>
                      <span className="font-semibold">{Math.round((data.competitive.competitive_density_score||0)*100)}%</span>
                    </div>
                    <Progress value={(data.competitive.competitive_density_score||0)*100} className="h-2" />
                    <p className="text-xs text-zinc-400 mt-1">Higher = more orgs competing for same funders</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-600">Distinctiveness</span>
                      <span className="font-semibold">{Math.round((data.competitive.distinctiveness_score||0)*100)}%</span>
                    </div>
                    <Progress value={(data.competitive.distinctiveness_score||0)*100} className="h-2" />
                    <p className="text-xs text-zinc-400 mt-1">Higher = more unique positioning vs peers</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Live Competition Landscape — 5-stage breakdown */}
            {compSummary && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Swords className="h-4 w-4 text-orange-500" />
                    Competition Landscape
                    <Badge className={`ml-auto text-xs ${
                      compSummary.competition_intensity === 'HIGH' ? 'bg-red-100 text-red-700' :
                      compSummary.competition_intensity === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>{compSummary.competition_intensity}</Badge>
                  </CardTitle>
                  <CardDescription>Live data · {compSummary.total_mirror_direct} direct/mirror competitors in your province</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Stage breakdown pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(STAGE_INFO).map(([key, info]) => {
                      const count = compSummary.counts?.[key] ?? 0;
                      if (count === 0) return null;
                      return (
                        <span key={key} className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${info.color}`}
                          title={info.desc}>
                          {info.label}: {count}
                        </span>
                      );
                    })}
                  </div>
                  {/* Top 5 direct competitors */}
                  {(directCompetitors?.direct?.length > 0 || directCompetitors?.mirror?.length > 0) && (
                    <div className="space-y-1.5 pt-1">
                      <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">Top Competitors</p>
                      {[...(directCompetitors.mirror || []), ...(directCompetitors.direct || [])].slice(0, 5).map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-zinc-50 last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-zinc-800 truncate">{c.competitor_name}</p>
                            <p className="text-zinc-400 truncate">{c.competitor_city}{c.competitor_city && c.competitor_province ? ', ' : ''}{c.competitor_province}</p>
                          </div>
                          <div className="flex items-center gap-1.5 ml-2 shrink-0">
                            <span className="font-bold text-orange-600">{c.stage_pct}%</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STAGE_INFO[c.stage_label?.toLowerCase()]?.color || 'bg-zinc-50 text-zinc-500 border-zinc-200'}`}>
                              {c.stage_label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-zinc-400">Stage 1 (Mirror) = funder must choose. Stage 2 (Direct) = same goal, overlapping pop. Stage 3+ = funder can fund both.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
